<script lang="ts">
  interface Props {
    label?: string;
    onclick: () => void;
    disabled?: boolean;
    /**
     * Bind Space to onclick + render a "[space]" hint inside the
     * button. Opt-in per mode — modes that ask the player to type or
     * read between rounds (quizzes, future text-input modes) should
     * leave this off so a spacebar press doesn't accidentally advance.
     *
     * Listener auto-mounts via $effect so it goes away the moment the
     * button unmounts (i.e. the round transitions back to awaiting-
     * guess state).
     */
    spaceHotkey?: boolean;
  }

  let {
    label = 'Next round',
    onclick,
    disabled = false,
    spaceHotkey = false,
  }: Props = $props();

  $effect(() => {
    if (!spaceHotkey || disabled) return;

    function handleKey(event: KeyboardEvent): void {
      if (event.key !== ' ' || event.repeat) return;
      // Modifier-held Space (Ctrl+Space, Alt+Space, Cmd+Space) is system
      // shortcut territory — never intercept.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      // Ignore Space while the player is typing somewhere (search,
      // future text fields). isContentEditable covers rich-text spots.
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      event.preventDefault();
      onclick();
    }

    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  });
</script>

<button class="next-btn" {disabled} {onclick}>
  <span class="next-btn__label">{label}</span>
  {#if spaceHotkey}
    <span class="next-btn__hotkey" aria-hidden="true">[space]</span>
  {/if}
  <span class="next-btn__arrow" aria-hidden="true">›</span>
</button>
